import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not found. Using localStorage fallback.');
}

export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

// Types for our cases table
export interface CaseRecord {
  id: string;
  user_id: string;
  user_email?: string;
  created_at: string;
  updated_at: string;
  status: 'processing' | 'completed' | 'error';
  vehicle_info?: string;
  location?: string;
  documents_count: number;
  images_count: number;
  fault_percentage?: number;
  estimated_cost?: number;
  results?: any;
  report_id?: string;
  model_used?: string;
}

// Types for user profile
export interface UserProfile {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  address?: string;
  city?: string;
  country?: string;
  created_at: string;
  updated_at: string;
}

// Types for user preferences
export interface UserPreferences {
  id: string;
  user_id: string;
  theme: 'light' | 'dark' | 'system';
  language: string;
  email_notifications: boolean;
  report_ready_notifications: boolean;
  weekly_digest: boolean;
  marketing_emails: boolean;
  default_report_format: 'pdf' | 'markdown' | 'json';
  include_raw_data: boolean;
  auto_download: boolean;
  share_analytics: boolean;
  save_history: boolean;
  created_at: string;
  updated_at: string;
}

// Profile service
export const profileService = {
  async getProfile(userId: string): Promise<UserProfile | null> {
    if (!supabase) {
      // Fallback to localStorage
      const stored = localStorage.getItem(`insura_profile_${userId}`);
      return stored ? JSON.parse(stored) : null;
    }

    const { data, error } = await supabase
      .from('insura_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching profile:', error);
    }

    return data;
  },

  async saveProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile | null> {
    if (!supabase) {
      // Fallback to localStorage
      const existing = localStorage.getItem(`insura_profile_${userId}`);
      const merged = {
        ...(existing ? JSON.parse(existing) : {}),
        ...profile,
        user_id: userId,
        updated_at: new Date().toISOString(),
      };
      localStorage.setItem(`insura_profile_${userId}`, JSON.stringify(merged));
      return merged as UserProfile;
    }

    // Check if profile exists
    const existing = await this.getProfile(userId);

    if (existing) {
      // Update
      const { data, error } = await supabase
        .from('insura_profiles')
        .update({ ...profile, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating profile:', error);
        throw error;
      }
      return data;
    } else {
      // Insert
      const { data, error } = await supabase
        .from('insura_profiles')
        .insert([{
          ...profile,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating profile:', error);
        throw error;
      }
      return data;
    }
  },
};

// Preferences service
export const preferencesService = {
  async getPreferences(userId: string): Promise<UserPreferences | null> {
    if (!supabase) {
      // Fallback to localStorage
      const stored = localStorage.getItem(`insura_preferences_${userId}`);
      return stored ? JSON.parse(stored) : null;
    }

    const { data, error } = await supabase
      .from('insura_preferences')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching preferences:', error);
    }

    return data;
  },

  async savePreferences(userId: string, preferences: Partial<UserPreferences>): Promise<UserPreferences | null> {
    if (!supabase) {
      // Fallback to localStorage
      const existing = localStorage.getItem(`insura_preferences_${userId}`);
      const merged = {
        ...(existing ? JSON.parse(existing) : {}),
        ...preferences,
        user_id: userId,
        updated_at: new Date().toISOString(),
      };
      localStorage.setItem(`insura_preferences_${userId}`, JSON.stringify(merged));
      return merged as UserPreferences;
    }

    // Check if preferences exist
    const existing = await this.getPreferences(userId);

    if (existing) {
      // Update
      const { data, error } = await supabase
        .from('insura_preferences')
        .update({ ...preferences, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating preferences:', error);
        throw error;
      }
      return data;
    } else {
      // Insert with defaults
      const defaults: Partial<UserPreferences> = {
        theme: 'light',
        language: 'en',
        email_notifications: true,
        report_ready_notifications: true,
        weekly_digest: false,
        marketing_emails: false,
        default_report_format: 'pdf',
        include_raw_data: false,
        auto_download: true,
        share_analytics: true,
        save_history: true,
      };

      const { data, error } = await supabase
        .from('insura_preferences')
        .insert([{
          ...defaults,
          ...preferences,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating preferences:', error);
        throw error;
      }
      return data;
    }
  },
};

// Helper functions for case operations
export const caseService = {
  async fetchCases(userId: string): Promise<CaseRecord[]> {
    if (!supabase) {
      console.warn('Supabase not configured, returning empty array');
      return [];
    }

    const { data, error } = await supabase
      .from('insura_cases')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching cases:', error);
      throw error;
    }

    return data || [];
  },

  async createCase(caseData: Partial<CaseRecord>): Promise<CaseRecord | null> {
    if (!supabase) {
      console.warn('Supabase not configured');
      return null;
    }

    const { data, error } = await supabase
      .from('insura_cases')
      .insert([caseData])
      .select()
      .single();

    if (error) {
      console.error('Error creating case:', error);
      throw error;
    }

    return data;
  },

  async updateCase(id: string, updates: Partial<CaseRecord>): Promise<CaseRecord | null> {
    if (!supabase) {
      console.warn('Supabase not configured');
      return null;
    }

    const { data, error } = await supabase
      .from('insura_cases')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating case:', error);
      throw error;
    }

    return data;
  },

  async deleteCase(id: string): Promise<boolean> {
    if (!supabase) {
      console.warn('Supabase not configured');
      return false;
    }

    const { error } = await supabase
      .from('insura_cases')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting case:', error);
      throw error;
    }

    return true;
  }
};

